/**
 * How much a location can be trusted.
 *
 * A mould's place is whatever its last scan said, which makes every location on
 * screen an assertion with an age. Today the screens do not show that age, so a
 * tool nobody has seen in three months renders exactly like one scanned this
 * morning - and on this catalog that is not a rare edge: most of the locations
 * on the board are months old or absent entirely.
 *
 * The fix is not to hide the old ones. Somebody standing at a rack would still
 * rather know where a tool probably is than be told nothing. The fix is to stop
 * claiming the two are equally good, which is what an unqualified place name
 * does.
 *
 * The bands are deliberately coarse. Nobody needs to know whether a scan was 41
 * or 46 days ago; they need to know whether to believe it.
 */

export type Freshness = 'fresh' | 'ageing' | 'stale' | 'cold' | 'never';

export interface FreshnessInfo {
  band: Freshness;
  /** Whole days since the last scan. Null when there has never been one. */
  days: number | null;
  /** For a table cell: short, and reads on its own. */
  label: string;
  /** What this means for someone about to rely on it. */
  meaning: string;
  /** Tailwind text colour, matched to the tones used elsewhere. */
  tone: string;
}

/** Everything seen at least this recently counts as current. */
export const FRESH_DAYS = 7;
/** The window the coverage figure reports on. */
export const COVERAGE_DAYS = 30;
const STALE_DAYS = 90;

export function daysSince(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / 86_400_000));
}

/** "4 days ago" up to a month, then months - a day count stops meaning anything. */
function phrase(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days <= COVERAGE_DAYS) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}

export function freshnessOf(lastScannedAt: string | null | undefined, now = Date.now()): FreshnessInfo {
  const days = daysSince(lastScannedAt, now);

  if (days === null) {
    return {
      band: 'never',
      days: null,
      label: 'Never scanned',
      meaning: 'Nobody has ever recorded seeing this tool. Its location is unknown.',
      tone: 'text-danger',
    };
  }
  if (days <= FRESH_DAYS) {
    return {
      band: 'fresh',
      days,
      label: `Seen ${phrase(days)}`,
      meaning: 'Recent enough to act on.',
      tone: 'text-success',
    };
  }
  if (days <= COVERAGE_DAYS) {
    return {
      band: 'ageing',
      days,
      label: phrase(days),
      meaning: `Last scanned ${days} days ago - probably still right, but unconfirmed this month.`,
      tone: 'text-muted-foreground',
    };
  }
  if (days <= STALE_DAYS) {
    return {
      band: 'stale',
      days,
      label: phrase(days),
      meaning: `Last scanned ${days} days ago - old enough to check before relying on it.`,
      tone: 'text-warning',
    };
  }
  return {
    band: 'cold',
    days,
    label: phrase(days),
    meaning: `Last scanned ${days} days ago. This is a guess, not a location.`,
    tone: 'text-danger',
  };
}

/** True when this tool counts towards "seen recently" in the coverage figure. */
export function isCovered(lastScannedAt: string | null | undefined, now = Date.now()): boolean {
  const days = daysSince(lastScannedAt, now);
  return days !== null && days <= COVERAGE_DAYS;
}

export interface Coverage {
  seen: number;
  total: number;
  /** Whole percent, rounded. 0 when the catalog is empty. */
  percent: number;
}

/**
 * How much of the catalog has actually been seen lately.
 *
 * The one number that says whether the system is being used. Every other figure
 * on the dashboard describes the records; this one describes whether anybody is
 * keeping them true. Without it the data can rot for a quarter and every screen
 * will go on looking confident.
 */
export function coverageOf(
  lastSeen: Array<string | null | undefined>,
  now = Date.now(),
): Coverage {
  const total = lastSeen.length;
  const seen = lastSeen.filter((d) => isCovered(d, now)).length;
  return { seen, total, percent: total ? Math.round((seen / total) * 100) : 0 };
}
